import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Clock, ExternalLink, Loader2, Megaphone, Pause, Pencil, Play } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import Loading from "components/loading";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "components/ui/dialog";
import { MIN_BOOST_BUDGET, naira } from "lib/boost";
import { useUserData } from "store/user-data";
import { CampaignDetail, getCampaignAction, placementLabel, requestCampaignChangeAction } from "action/ads-action";
import {
  AgeGenderBars,
  CampaignStatus,
  EmptyNote,
  Funnel,
  LocationList,
  PerformanceChart,
  PlacementTable,
  Section,
  StatTiles,
  shortDate,
  statTiles,
  timeAgo,
} from "./parts";

const isVideo = (url: string) => /\.(mp4|mov)(\?|$)/i.test(url);

const REQUEST_TEXT: Record<string, string> = {
  pause: "pause this ad",
  resume: "resume this ad",
  budget: "change the budget",
};

export default function StorboltCampaignDetailComponent() {
  const { campaignId = "" } = useParams();
  const { userData } = useUserData();
  const queryClient = useQueryClient();
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [sending, setSending] = useState<string | null>(null);

  const { data: response, isLoading } = useQuery({
    queryKey: ["ads-campaign", userData?.business_id, campaignId],
    queryFn: () => getCampaignAction(campaignId),
    enabled: !!userData?.business_id && !!campaignId,
  });
  const campaign = response?.error ? null : response?.body;

  const sendRequest = async (action: "pause" | "resume" | "budget", budget?: number) => {
    setSending(action);
    const result = await requestCampaignChangeAction(campaignId, { action, budget });
    setSending(null);
    if (result.error) {
      toast.error(result.error);
      return false;
    }
    queryClient.setQueryData(["ads-campaign", userData?.business_id, campaignId], result);
    toast.success("Request sent. Our team will apply it shortly.");
    return true;
  };

  if (isLoading) return <Loading />;
  if (!campaign) {
    return (
      <div className="space-y-4">
        <BackLink />
        <EmptyNote>We couldn't find this campaign.</EmptyNote>
      </div>
    );
  }

  const canPause = ["IN_REVIEW", "ACTIVE"].includes(campaign.status);
  const canResume = campaign.status === "PAUSED";
  const canChangeBudget = ["IN_REVIEW", "ACTIVE", "PAUSED"].includes(campaign.status);
  const pending = campaign.pending_request;
  const preview = campaign.creative_urls[0] || campaign.product_image;

  return (
    <div className="space-y-5">
      <BackLink />
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-2xl font-semibold tracking-tight text-default-900 md:text-[28px]">
              {campaign.title}
            </h1>
            <CampaignStatus campaign={campaign} />
          </div>
          <p className="mt-1.5 text-sm text-default-600">
            {campaign.last_synced_at ? `Data last synced ${timeAgo(campaign.last_synced_at)}` : "No results yet: they appear once the ad is live."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {campaign.live_ad_url && (
            <Button asChild variant="outline">
              <a href={campaign.live_ad_url} target="_blank" rel="noopener noreferrer">
                View live ad <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          )}
          {canPause && (
            <Button variant="outline" disabled={!!sending} onClick={() => sendRequest("pause")}>
              {sending === "pause" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Pause className="mr-2 h-4 w-4" />}
              Pause
            </Button>
          )}
          {canResume && (
            <Button variant="outline" disabled={!!sending} onClick={() => sendRequest("resume")}>
              {sending === "resume" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Resume
            </Button>
          )}
          {canChangeBudget && (
            <Button disabled={!!sending} onClick={() => setBudgetOpen(true)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit budget
            </Button>
          )}
        </div>
      </div>

      {pending && (
        <div className="flex items-start gap-3 rounded-20px bg-info/10 p-4 text-sm text-default-800">
          <Clock className="mt-0.5 h-4 w-4 flex-none text-info" />
          <span>
            You asked us to {REQUEST_TEXT[pending.action] ?? pending.action}
            {pending.action === "budget" && pending.budget ? ` to ${naira(pending.budget)}` : ""} {timeAgo(pending.requested_at)}.
            Our team will apply it shortly.
          </span>
        </div>
      )}
      {campaign.status === "REJECTED" && (
        <div className="rounded-20px bg-destructive/10 p-4 text-sm text-default-800">
          Meta didn't approve this ad. Our team will contact you about the next steps.
        </div>
      )}

      <StatTiles tiles={statTiles(campaign, campaign.metrics)} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Section title="Ad preview">
          <div className="overflow-hidden rounded-[14px] bg-default-100">
            {preview ? (
              isVideo(preview) ? (
                <video src={preview} controls playsInline className="aspect-square w-full object-cover" />
              ) : (
                <img src={preview} alt="Ad creative" className="aspect-square w-full object-cover" />
              )
            ) : (
              <div className="flex aspect-square flex-col items-center justify-center gap-2 p-6 text-center text-sm text-default-500">
                <Megaphone className="h-6 w-6" />
                Our team is creating your ad. It will appear here.
              </div>
            )}
          </div>
          {campaign.creative_urls.length > 1 && (
            <p className="mt-2 text-xs text-default-500">+ {campaign.creative_urls.length - 1} more file(s)</p>
          )}
        </Section>
        <Section title="From view to purchase">
          <Funnel totals={campaign} />
        </Section>
        <Section title="Campaign details">
          <DetailList campaign={campaign} />
        </Section>
      </div>

      <Section title="Performance over time">
        <PerformanceChart daily={campaign.daily} />
      </Section>

      <Section title="Results by placement">
        <PlacementTable rows={campaign.by_placement} />
      </Section>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Section title="Who saw your ad">
          <AgeGenderBars rows={campaign.by_age_gender} />
        </Section>
        <Section title="Top locations">
          <LocationList rows={campaign.by_location} />
        </Section>
      </div>

      <BudgetDialog
        open={budgetOpen}
        current={Number(campaign.budget_ngn)}
        sending={sending === "budget"}
        onClose={() => setBudgetOpen(false)}
        onSubmit={async (budget) => {
          if (await sendRequest("budget", budget)) setBudgetOpen(false);
        }}
      />
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/meta-ads" className="inline-flex items-center gap-1.5 text-sm font-medium text-default-600 hover:text-default-900">
      <ArrowLeft className="h-4 w-4" /> Back to Meta Ads
    </Link>
  );
}

function DetailList({ campaign }: { campaign: CampaignDetail }) {
  const rows: [string, React.ReactNode][] = [
    ["Promoting", campaign.promote === "PRODUCT" ? campaign.title : "Your storefront"],
    ["Creative", campaign.creative_type === "UPLOAD" ? "Your own files" : "Created by Storbolt"],
    ["Placements", campaign.placements.map(placementLabel).join(", ")],
    ["Budget", `${naira(campaign.budget_ngn)} over ${campaign.duration_days} days`],
    ["Runs", campaign.starts_at ? `${shortDate(campaign.starts_at)} – ${shortDate(campaign.ends_at) ?? "ongoing"}` : "Starts once it's live"],
    ["Ad ID", campaign.meta_ad_id || "-"],
  ];
  return (
    <dl className="space-y-3 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="flex gap-3">
          <dt className="w-24 flex-none text-default-500">{label}</dt>
          <dd className="min-w-0 flex-1 break-words font-medium text-default-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function BudgetDialog({ open, current, sending, onClose, onSubmit }: {
  open: boolean;
  current: number;
  sending: boolean;
  onClose: () => void;
  onSubmit: (budget: number) => void;
}) {
  const [value, setValue] = useState("");
  const budget = Number(value);
  const valid = budget >= MIN_BOOST_BUDGET;
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit budget</DialogTitle>
          <DialogDescription>
            Your budget is {naira(current)}. Ask for a new total and our team will update the ad. If it's higher, they'll
            send you a link to pay the difference.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) onSubmit(budget);
          }}
        >
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-default-500">₦</span>
            <Input
              autoFocus
              inputMode="numeric"
              value={value}
              onChange={(event) => setValue(event.target.value.replace(/[^\d]/g, "").slice(0, 9))}
              placeholder="New total budget"
              aria-label="New total budget"
              className="pl-7"
            />
          </div>
          <p className="text-xs text-default-500">Minimum {naira(MIN_BOOST_BUDGET)}.</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!valid || sending}>
              {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send request
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
